import { useCallback, useEffect, useRef } from 'react'
import { PiEyeBold, PiEyeSlashBold } from 'react-icons/pi'
import { useTranslation } from 'react-i18next'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useUserPreferences } from '../contexts/UserPreferencesContext'
import { BASE_GRAY, PRIMARY } from '../utils/constants/map.constants'
import { Position } from '../types/geoXp'

interface PatternChipProps {
  pattern: Position
  selectedPatterns: Position[]
  viewPattern: (pattern: Position, fitBounds?: boolean, hideOthers?: boolean) => void
  togglePattern: (pattern: Position) => void
}

export const PatternChip = ({ pattern, selectedPatterns, viewPattern, togglePattern }: PatternChipProps) => {
  const { darkMode } = useDarkMode()
  const { mapStyle } = useUserPreferences()
  const { t } = useTranslation()

  const selected = selectedPatterns.map((p) => p.id).includes(pattern.id)

  const doubleTapRef = useRef<NodeJS.Timeout | null>(null)
  const detectClick = useCallback((single: () => void, double: () => void) => {
    if (doubleTapRef.current) {
      clearTimeout(doubleTapRef.current)
      doubleTapRef.current = null
      double()
      return
    }

    doubleTapRef.current = setTimeout(() => {
      doubleTapRef.current = null
      single()
    }, 100)
  }, [])

  useEffect(() => () => {
    if (doubleTapRef.current) clearTimeout(doubleTapRef.current)
    doubleTapRef.current = null
  })

  return (
    <div
      className="flex flex-none items-stretch rounded-md font-bold uppercase cursor-pointer text-white overflow-hidden"
      style={{
        backgroundColor:
          pattern.color ?? (mapStyle === 'satellite' || darkMode ? BASE_GRAY[700] : PRIMARY[500]),
        opacity: selected ? 1 : 0.5,
      }}
    >
      <div
        className="px-4 py-2 rounded-l-md hover:opacity-70 select-none "
        onClick={() =>
          detectClick(
            () => viewPattern(pattern, true),
            () => viewPattern(pattern, true, true)
          )
        }
        onDoubleClick={() => viewPattern(pattern, true, true)}
      >
        {pattern.id === 'default' ? t('experience.otherAgami') : pattern.label}
      </div>
      <div
        className="px-4 py-2 rounded-r-md flex justify-center items-center border-l-white border-l-[1px] hover:opacity-70"
        onClick={() => togglePattern(pattern)}
      >
        {selected ? <PiEyeBold fontSize="16px" /> : <PiEyeSlashBold fontSize="16px" />}
      </div>
    </div>
  )
}
